import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { getSupabaseClient } from '../../../config/supabase';
import { useEndSessionDebug } from '../../../context/EndSessionDebugContext';
import HabitPrompt from './HabitPrompt';
import OpenFeedback from './OpenFeedback';
import PartnerRating from './PartnerRating';
import './ContentCheckout.css';

async function saveFeedback({ partnerCompatibility, openFeedback, wantsMoreSessions }) {
  try {
    const slug = window.location.pathname.split('/').filter(Boolean).pop();
    const supabase = await getSupabaseClient();
    if (!supabase || !slug) return;

    const { data: member } = await supabase
      .from('room_members')
      .select('session_id, user_id')
      .eq('slug', slug)
      .limit(1)
      .single();

    await supabase.from('session_feedback').insert({
      session_id: member?.session_id ?? null,
      reviewer_id: member?.user_id ?? null,
      partner_compatibility: partnerCompatibility ?? null,
      open_feedback: openFeedback ?? null,
      wants_more_sessions: wantsMoreSessions ?? null,
    });
  } catch {
    // falha silenciosa — não bloqueia o fluxo
  }
}

const summaryStyle = {
  fontSize: 18,
  color: 'rgba(255,255,255,0.8)',
  fontFamily: "'League Spartan', Arial, sans-serif",
  textAlign: 'center',
  maxWidth: 360,
  lineHeight: 1.5,
};

const continueBtnStyle = {
  padding: '12px 28px',
  fontSize: 17,
  fontFamily: "'League Spartan', Arial, sans-serif",
  fontWeight: 700,
  color: '#55B4BE',
  backgroundColor: '#fff',
  border: 'none',
  borderRadius: 100,
  cursor: 'pointer',
};

const doneStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
};

export default function ContentCheckout() {
  const { isFirstSession } = useEndSessionDebug();

  // summary → (partner_rating se 1ª sessão) → open_feedback → (habit se 1ª sessão) → done
  const [subStep, setSubStep] = useState('summary');
  const [partnerRating, setPartnerRating] = useState(null);
  const [openFeedbackText, setOpenFeedbackText] = useState(null);
  const [wantsHabit, setWantsHabit] = useState(null);

  const handleSummaryNext = useCallback(() => {
    setSubStep(isFirstSession ? 'partner_rating' : 'open_feedback');
  }, [isFirstSession]);

  const handlePartnerRating = useCallback((rating) => {
    setPartnerRating(rating);
    setSubStep('open_feedback');
  }, []);

  const handleFeedbackSubmit = useCallback((text) => {
    setOpenFeedbackText(text);
    if (isFirstSession) {
      setSubStep('habit');
    } else {
      saveFeedback({ partnerCompatibility: partnerRating, openFeedback: text, wantsMoreSessions: null });
      setSubStep('done');
    }
  }, [partnerRating, isFirstSession]);

  const handleHabitAnswer = useCallback((answer) => {
    saveFeedback({ partnerCompatibility: partnerRating, openFeedback: openFeedbackText, wantsMoreSessions: answer });
    setWantsHabit(answer);
    setSubStep('done');
  }, [partnerRating, openFeedbackText]);

  return (
    <motion.div
      className="checkout-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
    >
      <div className="checkout-card">
        {subStep === 'summary' && (
          <div className="checkout-section">
            <span style={{ fontSize: 48 }}>📊</span>
            <span style={summaryStyle}>
              Estamos trabalhando para te dar um resumo pós-sessão, mas ainda não temos.
            </span>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" style={continueBtnStyle} onClick={handleSummaryNext}>
                tá bom →
              </button>
              <button type="button" style={continueBtnStyle} onClick={handleSummaryNext}>
                poxa, tá bom →
              </button>
            </div>
          </div>
        )}

        {subStep === 'partner_rating' && (
          <PartnerRating onConfirm={handlePartnerRating} />
        )}

        {subStep === 'open_feedback' && (
          <OpenFeedback onSubmit={handleFeedbackSubmit} />
        )}

        {subStep === 'habit' && (
          <HabitPrompt onAnswer={handleHabitAnswer} />
        )}

        {subStep === 'done' && wantsHabit === true && (
          <div style={doneStyle}>
            <span style={{ fontSize: 40 }}>🎉</span>
            <span className="checkout-title">Combinado então!</span>
            <span className="checkout-subtitle">Salve os lembretes na nova aba. A gente manda os detalhes pelo WhatsApp.<br/>Até semana que vem!</span>
          </div>
        )}

        {subStep === 'done' && wantsHabit === false && (
          <div style={doneStyle}>
            <span style={{ fontSize: 40 }}>😕</span>
            <span className="checkout-title">Tudo bem.</span>
            <span className="checkout-subtitle">Se mudar de ideia é só chamar no WhatsApp.</span>
          </div>
        )}

        {subStep === 'done' && wantsHabit === null && (
          <div style={doneStyle}>
            <span className="checkout-title">Até mais!</span>
            <span className="checkout-subtitle">Obrigado por tirar um tempo para avaliar.</span>
            <span className="checkout-subtitle">Mandaremos lembretes pelo seu WhatsApp!</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
